import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { Target } from 'lucide-react';
import PageSkeleton from '../../../components/PageSkeleton';

import {
  useServeOverview,
} from '../../../utils/api-hooks/analytics-serve';
// Reuse the trend helper from the chart-level scorecards so the icon set,
// color palette, and zero/null semantics stay in lockstep.
import { trendStyle } from './KpiScorecards';
import { isoWeekLabel, formatRangeShort, formatRangeFull, captionText, captionPart } from './kpiFormat';
import CaptionLine from './CaptionLine';

const toNum = (v) => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatIsoWeek = (isoWeek) => {
  if (!isoWeek) return null;
  const m = /W(\d+)/.exec(String(isoWeek));
  return m ? m[1] : null;
};

const formatUsd = (v) => {
  const n = toNum(v);
  if (n == null) return '—';
  return `$${n.toFixed(2)}`;
};

const formatCompact = (v) => {
  const n = toNum(v);
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  return `${n.toFixed(0)}`;
};

/** `analytics_overview_endpoint` returns `cards["overview.*"]`; older shape used `overview: { fob_price, ... }`. */
function pickOverviewSections(data) {
  if (!data || typeof data !== 'object') {
    return { fob: {}, terminal: {}, weeklyVolume: {}, fractionalDeltaPct: false };
  }
  const cards = data.cards;
  if (cards && typeof cards === 'object') {
    return {
      fob: cards['overview.fob_price'] || {},
      terminal: cards['overview.terminal_price'] || {},
      weeklyVolume: cards['overview.weekly_volume'] || {},
      fractionalDeltaPct: true,
    };
  }
  const o = data.overview || data;
  return {
    fob: o.fob_price || o.fob || {},
    terminal: o.terminal_price || o.terminal || {},
    weeklyVolume: o.weekly_volume || o.volume || {},
    fractionalDeltaPct: false,
  };
}

function specSize(spec) {
  if (!spec || typeof spec !== 'object') return null;
  return spec.size ?? spec.item_size ?? null;
}

/**
 * One cell of the hero strip. Cells are dividers-only — the border/radius lives
 * on the shared 138px container, so nothing here may add vertical chrome.
 */
const Tile = ({ label, value, deltaPct, dateLabel, dateTitle, prevLine, specParts, footer, showDivider, fallbackIcon }) => {
  const delta = toNum(deltaPct);
  const style = trendStyle(delta);
  const heroIsDelta = delta != null;
  const FallbackIcon = fallbackIcon;
  const signedPct = (n) => {
    const abs = Math.abs(n);
    const digits = abs >= 10 ? 1 : 1;
    const sign = n > 0 ? '+' : n < 0 ? '−' : '';
    return `${sign}${abs.toFixed(digits)}%`;
  };

  return (
    <div
      className={`flex min-w-0 flex-1 flex-col overflow-hidden px-[16px] py-[14px] ${
        showDivider ? 'sm:border-l sm:border-[#E2E2E2] sm:dark:border-[#3a3c3b]' : ''
      }`}
    >
      {/* Label row — IconBadge pinned top-right, mirrors TileFrame layout
          used by FobPricePulseScorecards et al. */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 truncate whitespace-nowrap text-[14px] text-[#282A29] dark:text-white" title={typeof label === 'string' ? label : undefined}>
          {label}
        </div>
        <div className="flex shrink-0 items-center gap-2">
        {dateLabel ? (
          <span
            className="shrink-0 whitespace-nowrap text-[11px] text-[#828382] dark:text-gray-400"
            title={dateTitle || undefined}
          >
            {dateLabel}
          </span>
        ) : null}
        {style ? (
          <span
            className={`shrink-0 inline-flex items-center justify-center rounded-md p-1 ${style.badgeClass}`}
            aria-hidden
          >
            <style.Icon size={14} strokeWidth={2.25} className={style.textClass} />
          </span>
        ) : FallbackIcon ? (
          <span
            className="shrink-0 inline-flex items-center justify-center rounded-md bg-[#F1F3F2] p-1 dark:bg-[#282a29]"
            aria-hidden
          >
            <FallbackIcon size={14} strokeWidth={2.25} className="text-[#828382] dark:text-gray-400" />
          </span>
        ) : (
          // Reserve the badge width (22px) so the date column lines up across cells.
          <span className="inline-block h-[22px] w-[22px] shrink-0" aria-hidden />
        )}
        </div>
      </div>

      {/* Hero: signed percent for movers, raw value for active-origins. */}
      {heroIsDelta ? (
        <div className={`mt-[8px] whitespace-nowrap analytics-hero-value font-semibold ${style?.textClass || 'text-[#282A29] dark:text-white'}`}>
          {signedPct(delta)}
        </div>
      ) : (
        <div className="mt-[8px] truncate analytics-hero-value font-semibold text-[#282A29] dark:text-white">
          {value}
        </div>
      )}

      {prevLine ? (
        <div
          className="mt-[6px] min-w-0 break-words [overflow-wrap:anywhere] text-[11px] leading-[14px] tabular-nums text-[#828382] dark:text-gray-400"
          title={prevLine}
        >
          {prevLine}
        </div>
      ) : null}

      {specParts && specParts.length ? (
        <CaptionLine
          parts={specParts}
          lines={2}
          className="text-[11px] leading-[14px] text-[#828382] dark:text-gray-400"
          title={captionText(specParts)}
        />
      ) : null}

      {footer ? (
        <div className="mt-[6px] text-[11px] leading-[14px] text-[#828382] dark:text-gray-400">
          {footer}
        </div>
      ) : null}
    </div>
  );
};

/**
 * View-2 "Analytics Tab" hero scorecards (top 4 tiles).
 * Data source: analytics_serve_* endpoints.
 *
 * Charts below this section remain the standard app charts (CHART_REGISTRY).
 */
const AnalyticsTabHeroScorecards = ({ apiParams }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);

  const commodityKey = apiParams?.commodity || '';

  const overviewQ = useServeOverview(commodityKey, {
    email: apiParams?.email,
    dateStart: apiParams?.dateStart,
    dateEnd: apiParams?.dateEnd,
  });
  const isLoading = overviewQ.isLoading || overviewQ.isFetching;

  const tiles = useMemo(() => {
    const { fob, terminal, weeklyVolume, fractionalDeltaPct } = pickOverviewSections(overviewQ.data);

    const deltaPctUi = (card) => {
      const raw = toNum(card?.delta?.pct);
      if (raw == null) return null;
      // New API: decimal rate (0.2 => 20%). Legacy: may already be percent points.
      if (fractionalDeltaPct && Math.abs(raw) <= 1) return raw * 100;
      return raw;
    };

    const metricUnavailable = (card) => card && card.data_available === false;

    const fobValue = metricUnavailable(fob) ? '—' : formatUsd(fob?.primary_metric?.value);
    const termValue = metricUnavailable(terminal) ? '—' : formatUsd(terminal?.primary_metric?.value);
    const weeklyVolValue = metricUnavailable(weeklyVolume)
      ? '—'
      : formatCompact(weeklyVolume?.primary_metric?.value);

    const activeOriginsValue = (() => {
      if (metricUnavailable(weeklyVolume)) return '—';
      const n = toNum(weeklyVolume?.meta?.current_active_origins);
      return n != null ? `${n}` : '—';
    })();

    // Meaningfulness filter: drop null/undefined, empty strings, and the
    // known "missing" sentinels ("N/A", "Not Classified") that the backend
    // may still pass through for certain source rows.
    const isMeaningful = (v) => {
      if (v == null) return false;
      if (typeof v !== 'string') return true;
      const t = v.trim();
      if (t === '') return false;
      const upper = t.toUpperCase();
      return upper !== 'N/A' && t !== 'Not Classified';
    };

    // `W{prev} {prevValue} → W{cur} {curValue}` line (11px muted).
    const buildPrevLine = (card, formatter) => {
      const cur = formatIsoWeek(card?.meta?.current_iso_week);
      const prev = formatIsoWeek(card?.meta?.prev_iso_week);
      const curVal = card?.primary_metric?.value;
      const prevVal = card?.meta?.prev_price ?? card?.meta?.prev_kg;
      if (!cur || !prev || curVal == null || prevVal == null) return null;
      return `W${prev} ${formatter(prevVal)} → W${cur} ${formatter(curVal)}`;
    };

    // Spec caption parts: every meaningful cascade dim (no week). Rendered by
    // CaptionLine, which drops exact duplicates and breaks only between parts
    // or at word boundaries.
    const buildSpecParts = (card, specKeys) => {
      const spec = card?.spec || {};
      return specKeys
        .map((k) => (k === 'size' ? specSize(spec) : spec?.[k]))
        .filter(isMeaningful)
        .map((text) => captionPart(String(text)));
    };

    const rangeLabel = formatRangeShort(apiParams?.dateStart, apiParams?.dateEnd);
    const rangeTitle = formatRangeFull(apiParams?.dateStart, apiParams?.dateEnd);
    const weekOrRange = (card) => isoWeekLabel(String(card?.meta?.current_iso_week ?? '')) || rangeLabel;
    const dateTitleFor = (card) =>
      isoWeekLabel(String(card?.meta?.current_iso_week ?? '')) ? undefined : rangeTitle;

    const FOB_DIMS = [
      'variety', 'size', 'grade', 'package',
      'storage', 'condition', 'environment', 'quality', 'appearance', 'properties', 'type',
    ];
    const TERMINAL_DIMS = ['location', ...FOB_DIMS];
    const VOLUME_DIMS = ['variety', 'package', 'environment', 'type'];

    return [
      {
        id: 'fob',
        label: t('analyticsTab.hero.fobPrice', 'Top FOB mover'),
        value: fobValue,
        deltaPct: deltaPctUi(fob),
        dateLabel: weekOrRange(fob),
        dateTitle: dateTitleFor(fob),
        prevLine: buildPrevLine(fob, formatUsd),
        specParts: buildSpecParts(fob, FOB_DIMS),
      },
      {
        id: 'terminal',
        label: t('analyticsTab.hero.terminalPrice', 'Top terminal mover'),
        value: termValue,
        deltaPct: deltaPctUi(terminal),
        dateLabel: weekOrRange(terminal),
        dateTitle: dateTitleFor(terminal),
        prevLine: buildPrevLine(terminal, formatUsd),
        specParts: buildSpecParts(terminal, TERMINAL_DIMS),
      },
      {
        id: 'weeklyVolume',
        label: t('analyticsTab.hero.weeklyVolume', 'Top volume mover'),
        value: weeklyVolValue,
        deltaPct: deltaPctUi(weeklyVolume),
        dateLabel: weekOrRange(weeklyVolume),
        dateTitle: dateTitleFor(weeklyVolume),
        prevLine: buildPrevLine(weeklyVolume, formatCompact),
        specParts: buildSpecParts(weeklyVolume, VOLUME_DIMS),
      },
      {
        id: 'activeOrigins',
        label: t('analyticsTab.hero.activeOrigins', 'Active origins'),
        value: activeOriginsValue,
        deltaPct: null,
        dateLabel: weekOrRange(weeklyVolume),
        dateTitle: dateTitleFor(weeklyVolume),
        prevLine: null,
        specParts: null,
        footer: t('analyticsTab.hero.activeOriginsFooter', 'Actively shipping to USA'),
        // No delta on this tile, so `trendStyle` yields no badge — keep the
        // top-right icon slot filled to match the other three cells.
        fallbackIcon: Target,
      },
    ];
  }, [overviewQ.data, t, apiParams?.dateStart, apiParams?.dateEnd]);

  // One shared card at the design height; cells are split by 1px rules.
  // Stacks (and drops the height floor) below `sm`, where four columns can't fit.
  //
  // `min-h`, not `h`: every line in here scales with the global letter-size
  // control (`.analytics-hero-value` plus the `text-[12px]`/`text-[14px]` rows),
  // and a hard 138px with `overflow-hidden` cut the spec and prev-week lines off
  // at the larger settings. The floor keeps the default rendering identical and
  // only lets the card grow when the text genuinely needs it.
  return (
    <div
      className="w-full overflow-hidden rounded-[8px] border border-[#E2E2E2] bg-white sm:min-h-[138px] dark:border-[#3a3c3b] dark:bg-[#191919]"
      style={{ color: theme.textColor }}
    >
      {isLoading ? (
        <PageSkeleton shape="hero" />
      ) : (
        <div className="flex h-full flex-col divide-y divide-[#E2E2E2] sm:flex-row sm:divide-y-0 dark:divide-[#3a3c3b]">
          {tiles.map((c, idx) => (
            <Tile
              key={c.id}
              label={c.label}
              value={c.value}
              deltaPct={c.deltaPct}
              dateLabel={c.dateLabel}
              dateTitle={c.dateTitle}
              prevLine={c.prevLine}
              specParts={c.specParts}
              footer={c.footer}
              fallbackIcon={c.fallbackIcon}
              showDivider={idx > 0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AnalyticsTabHeroScorecards;

